import { Dispatch, useCallback, useEffect } from "react";
import { SearchInput, SelectControl } from "../Common";
import Checkbox from "@mui/material/Checkbox";
import {
	useClinicContext,
	useDoctorContext,
	useDoctorsFiltersContext
} from "../../contexts";
import { Clinic, DoctorUser } from "../../models";

interface props {
	clinic: Clinic | undefined;
	setClinic: React.Dispatch<React.SetStateAction<Clinic | undefined>>;
	setFilteredDoctors: Dispatch<React.SetStateAction<DoctorUser[]>>;
}

export const DoctorsFilter = ({
	clinic,
	setClinic,
	setFilteredDoctors
}: props) => {
	const { users } = useDoctorContext();
	const { userClinics } = useClinicContext();
	const { filters, setFilters } = useDoctorsFiltersContext();

	useEffect(() => {
		if (!users) {
			return;
		}

		const updatedFilteredDoctors = [...users].filter(
			(user) =>
				(!filters.new || user.doctor.bpDoctors.length === 0) &&
				(!filters.active || user.doctor.status) &&
				(!filters.faceToFace ||
					(user.doctor.bpDoctors &&
						user.doctor.bpDoctors.some((doctor) => doctor.takingFaceToFace))) &&
				(!filters.telehealth ||
					(user.doctor.bpDoctors &&
						user.doctor.bpDoctors.some((doctor) => doctor.takingTelehealth))) &&
				(!filters.localTelehealth ||
					(user.doctor.bpDoctors &&
						user.doctor.bpDoctors.some(
							(doctor) => doctor.takingLocalTelehealth
						))) &&
				(!filters.appointments ||
					(user.doctor.bpDoctors &&
						user.doctor.bpDoctors.some(
							(doctor) => doctor.takingAppointments
						))) &&
				(filters.nameParts.length === 0 ||
					filters.nameParts.some(
						(name) => user.displayName.toLowerCase().indexOf(name) >= 0
					))
		);
		setFilteredDoctors(updatedFilteredDoctors || []);
	}, [filters, setFilteredDoctors, users]);

	const handleSearchValue = useCallback(
		(value: string) => {
			const nameParts: string[] = value
				.trim()
				.split(",")
				.map((part: string) => part.toLowerCase().trim())
				.filter((part: string) => part.length > 0);
			setFilters((prev) => ({ ...prev, nameSearch: value, nameParts }));
		},
		[setFilters]
	);

	const toggleFilter = useCallback(
		(name: string, checked: boolean) => {
			switch (name) {
				case "faceToFace":
					setFilters((prev) => ({ ...prev, faceToFace: checked }));
					break;
				case "telehealth":
					setFilters((prev) => ({ ...prev, telehealth: checked }));
					break;
				case "localTelehealth":
					setFilters((prev) => ({ ...prev, localTelehealth: checked }));
					break;
				case "appointments":
					setFilters((prev) => ({ ...prev, appointments: checked }));
					break;
				case "active":
					setFilters((prev) => ({ ...prev, active: checked }));
					break;
				case "new":
					setFilters((prev) => ({ ...prev, new: checked }));
					break;
				default:
					break;
			}
		},
		[setFilters]
	);

	return (
		<div className="ch-card ch-border p-[24px] mb-4 w-full flex flex-col gap-8">
			<div className="flex flex-row gap-4">
				<div style={{ width: 250 }}>
					<SelectControl
						label="Centre"
						options={[
							{
								label: "All",
								value: "0"
							}
						].concat(
							(userClinics ?? []).map((userClinic) => ({
								label: userClinic.name,
								value: userClinic.id.toString()
							}))
						)}
						value={clinic?.id.toString() ?? "0"}
						handleChange={(value) =>
							setClinic(
								userClinics?.find(
									(userClinic) => userClinic.id === Number(value)
								)
							)
						}
					/>
				</div>
				<div style={{ width: 400 }}>
					<SearchInput
						label="Name"
						value={filters.nameSearch}
						handleSearchValueChange={handleSearchValue}
					/>
				</div>
			</div>
			<div className="flex flex-row justify-between">
				<div className="flex flex-col">
					<div>Consult Types</div>
					<div className="flex flex-row gap-8">
						<div className="ch-centre-checkbox" style={{ width: "unset" }}>
							<Checkbox
								checked={filters.faceToFace}
								onChange={(e, checked) => toggleFilter(`faceToFace`, checked)}
							/>
							Face-To-Face
						</div>
						<div className="ch-centre-checkbox" style={{ width: "unset" }}>
							<Checkbox
								checked={filters.telehealth}
								onChange={(e, checked) => toggleFilter(`telehealth`, checked)}
							/>
							Telehealth
						</div>
						<div className="ch-centre-checkbox" style={{ width: "unset" }}>
							<Checkbox
								checked={filters.localTelehealth}
								onChange={(e, checked) =>
									toggleFilter(`localTelehealth`, checked)
								}
							/>
							Local Telehealth
						</div>
						<div className="ch-centre-checkbox" style={{ width: "unset" }}>
							<Checkbox
								checked={filters.appointments}
								onChange={(e, checked) => toggleFilter(`appointments`, checked)}
							/>
							Appointments
						</div>
					</div>
				</div>

				<div className="flex flex-col">
					<div>Status</div>
					<div className="flex flex-row gap-8">
						<div className="ch-centre-checkbox" style={{ width: "unset" }}>
							<Checkbox
								checked={filters.new}
								onChange={(e, checked) => toggleFilter(`new`, checked)}
							/>
							New
						</div>
						<div className="ch-centre-checkbox" style={{ width: "unset" }}>
							<Checkbox
								checked={filters.active}
								onChange={(e, checked) => toggleFilter(`active`, checked)}
							/>
							Active
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
